/**
 * Phase 2C.2A — Google Places Autocomplete (New), shipping-address
 * UX assistance ONLY, loaded on the BPF Completion page alone (see
 * sections/bpf-completion.liquid, which only includes this file when
 * settings.bpf_google_places_api_key is configured).
 *
 * AUTHORITY: this script never authorizes anything. Backend shipping
 * validation (partner-production-order.ts's validateShippingAddress())
 * remains the sole authority regardless of whether this script loads,
 * works, or is used at all — a customer can always type the entire
 * address manually into the same underlying fields and submit
 * successfully with this script absent, blocked by an ad blocker, or
 * erroring for any reason. Nothing here ever disables or replaces
 * manual entry; it only offers suggestions on top of it.
 *
 * PRIVACY: zero network calls of its own except to Google's own Maps/
 * Places endpoints (loaded via Google's official bootstrap loader).
 * Never sends a typed query or a selected address to BPF telemetry,
 * GA4, or any BPF-owned log — this file has no such calls at all.
 * Session tokens are handled entirely internally by Google's own
 * PlaceAutocompleteElement widget (documented as automatic for both
 * the Autocomplete and the follow-up Place Details request) — no
 * manual token plumbing exists or is needed here.
 *
 * SECURITY: window.BPF_GOOGLE_PLACES_API_KEY is a BROWSER key,
 * intentionally public in the page source — protected by Google Cloud
 * HTTP-referrer restrictions (bestpetsforever.com and the *.myshopify.com
 * preview domain only), scoped to exactly two APIs (Maps JavaScript
 * API, Places API (New)), never by secrecy. It cannot read/write any
 * BPF data and authorizes nothing on its own.
 *
 * COUNTRY SCOPE — CORRECTION (found live in Chrome QA): Google's
 * Places Autocomplete (New) documents `includedRegionCodes` as
 * accepting "up to 15 CLDR two-character region codes" — BPF's full,
 * live-audited 28-country shipping allowlist exceeds that, and every
 * request was rejected (HTTP 400) as a result. The fix is NOT to trim
 * the list — the backend's 28-country allowlist
 * (partner-production-order.ts's SHIPPING_ALLOWED_COUNTRY_CODES)
 * remains fully authoritative and unchanged. Instead, Google is only
 * ever asked to restrict results to the ONE country currently selected
 * in the Country dropdown (always well under the 15-code limit),
 * re-created every time that selection changes. The 28-country
 * dropdown itself is the real allowlist UI; Google's own restriction
 * is a single-country autocomplete convenience layered on top of it.
 *
 * CANONICAL FIELD — CORRECTION (found live in Chrome QA): there is
 * exactly ONE authoritative Address Line 1 form value —
 * #bpf-completion-address1's own FormData entry, the same as every
 * other shipping field (see bpf-completion.js's collectShippingAddress(),
 * which now reads it with no special case at all). A prior version of
 * this file left that canonical input empty (hidden but still
 * `required`) once the visible autocomplete element held the real
 * value — Chrome's native constraint validation silently refuses to
 * submit a form containing a required, invalid, non-focusable
 * (`hidden`) control, so the Submit button looked enabled but clicking
 * it produced no request at all, with no `submit` event ever firing
 * for any JS to catch. Fixed by keeping the canonical input's value
 * continuously synced FROM the visible autocomplete element: on every
 * `gmp-select` (the real, structured street address), and again,
 * defensively, the instant the Submit button is activated (covering
 * free-typed text the customer never turned into a selection) — so
 * native validation always sees the same value the customer can see.
 */
(function () {
  'use strict';

  var apiKey = window.BPF_GOOGLE_PLACES_API_KEY;
  if (!apiKey) return; // no key configured — plain manual entry only, nothing to enhance

  var DEFAULT_COUNTRY_CODE = 'US';

  function byId(id) {
    return document.getElementById(id);
  }

  /**
   * Google's own documented bootstrap loader (unmodified) — lazily
   * injects the Maps JavaScript API script tag at most once, exposing
   * google.maps.importLibrary(). No BPF logic lives in this function.
   */
  function loadGoogleMapsBootstrap(key) {
    if (window.google && window.google.maps && window.google.maps.importLibrary) return;
    (function (g) {
      var h, a, k, p = 'The Google Maps JavaScript API', c = 'google', l = 'importLibrary', q = '__ib__', m = document, b = window;
      b = b[c] || (b[c] = {});
      var d = b.maps || (b.maps = {}), r = new Set(), e = new URLSearchParams(), u = function () {
        return h || (h = new Promise(function (f, n) {
          a = m.createElement('script');
          e.set('libraries', [...r] + '');
          for (k in g) e.set(k.replace(/[A-Z]/g, function (t) { return '_' + t[0].toLowerCase(); }), g[k]);
          e.set('callback', c + '.maps.' + q);
          a.src = 'https://maps.googleapis.com/maps/api/js?' + e;
          d[q] = f;
          a.onerror = function () { h = n(Error(p + ' could not load.')); };
          a.nonce = m.querySelector('script[nonce]') ? m.querySelector('script[nonce]').nonce : '';
          m.head.append(a);
        }));
      };
      d[l] ? void 0 : (d[l] = function (f) { for (var n = arguments.length, args = new Array(n > 1 ? n - 1 : 0), i = 1; i < n; i++) args[i - 1] = arguments[i]; return r.add(f) && u().then(function () { return d[l].apply(d, [f].concat(args)); }); });
    })({ key: key, v: 'weekly' });
  }

  function findAddressComponent(components, type) {
    for (var i = 0; i < (components || []).length; i++) {
      if (components[i].types && components[i].types.indexOf(type) !== -1) return components[i];
    }
    return null;
  }

  /** The CURRENTLY selected shipping country — read live, every time it's needed, never cached across a country change. Falls back to the documented default (US) if the dropdown is momentarily unpopulated (e.g. this script's own init racing the context fetch that populates it — see bpf-completion.js's populateCountrySelect()). */
  function currentCountryCode() {
    var select = byId('bpf-completion-country');
    var value = select && select.value ? select.value : '';
    return (value || DEFAULT_COUNTRY_CODE).toLowerCase();
  }

  function populateFieldsFromAddressComponents(components, manualInput, visibleValue) {
    var streetNumber = findAddressComponent(components, 'street_number');
    var route = findAddressComponent(components, 'route');
    var city = findAddressComponent(components, 'locality') || findAddressComponent(components, 'postal_town');
    var province = findAddressComponent(components, 'administrative_area_level_1');
    var postalCode = findAddressComponent(components, 'postal_code');
    var country = findAddressComponent(components, 'country');

    // The CANONICAL field — always written on a selection, never left
    // stale/empty. Prefer the structured street_number + route; if
    // Google didn't return either (some places don't have a numbered
    // street), fall back to whatever the visible box now shows rather
    // than leaving the real, required form field blank.
    if (manualInput) {
      var street = [streetNumber && streetNumber.longText, route && route.longText].filter(Boolean).join(' ');
      manualInput.value = street || visibleValue || manualInput.value;
    }

    // Only ever SET a field Google actually returned — a field Google
    // couldn't supply is left exactly as-is, available for manual
    // entry, never invented/blanked/guessed.
    var cityField = document.querySelector('[name="city"]');
    if (cityField && city) cityField.value = city.longText;
    var provinceField = document.querySelector('[name="province"]');
    if (provinceField && province) provinceField.value = province.shortText || province.longText;
    var postalField = document.querySelector('[name="postal_code"]');
    if (postalField && postalCode) postalField.value = postalCode.longText;
    var countryField = byId('bpf-completion-country');
    if (countryField && country && country.shortText) countryField.value = country.shortText.toUpperCase();
  }

  function init() {
    var manualInput = byId('bpf-completion-address1');
    var countrySelect = byId('bpf-completion-country');
    var submitButton = byId('bpf-completion-submit');
    if (!manualInput || !manualInput.parentNode) return;

    loadGoogleMapsBootstrap(apiKey);
    if (!window.google || !window.google.maps || !window.google.maps.importLibrary) return;

    window.google.maps
      .importLibrary('places')
      .then(function (places) {
        var PlaceAutocompleteElement = places.PlaceAutocompleteElement;
        if (!PlaceAutocompleteElement) return;

        var autocomplete = null;
        var slot = manualInput; // the element the current autocomplete instance sits immediately before; starts as the manual input itself

        function attachSelectHandler(el) {
          el.addEventListener('gmp-select', function (event) {
            var prediction = event.placePrediction;
            if (!prediction) return;
            var visibleValue = typeof el.value === 'string' ? el.value : '';
            prediction
              .toPlace()
              .fetchFields({ fields: ['addressComponents'] })
              .then(function (result) {
                var components = (result && result.place && result.place.addressComponents) || [];
                populateFieldsFromAddressComponents(components, manualInput, visibleValue);
              })
              .catch(function () {
                // Even if structured field lookup fails, the customer
                // DID select something — still sync the canonical
                // field from the visible box's own text rather than
                // leaving it blank+required.
                if (manualInput && visibleValue) manualInput.value = visibleValue;
              });
          });
        }

        /**
         * (Re)creates the autocomplete element restricted to exactly
         * ONE country — never the full shipping allowlist (see this
         * file's own header comment on the 15-code limit). Called once
         * on init and again every time the Country dropdown changes,
         * so a stale element (and any text/selection state it was
         * holding for the PREVIOUS country) is never left active.
         */
        function createAutocomplete(regionCode) {
          var next = new PlaceAutocompleteElement({ includedRegionCodes: [regionCode] });
          next.id = 'bpf-completion-address1-autocomplete';
          next.setAttribute('placeholder', 'Start typing your address…');
          attachSelectHandler(next);

          if (autocomplete && autocomplete.parentNode) {
            autocomplete.parentNode.replaceChild(next, autocomplete);
          } else {
            manualInput.hidden = true;
            manualInput.parentNode.insertBefore(next, manualInput);
          }
          autocomplete = next;
          slot = next;
          return next;
        }

        // The manual input is NEVER removed — only visually hidden
        // once the first autocomplete instance is successfully
        // created below. If anything above this line throws/rejects,
        // the manual input was never touched and remains fully
        // functional (the .catch() below preserves this too).
        createAutocomplete(currentCountryCode());

        // Defensive backstop for the ONE case gmp-select can't cover:
        // the customer typed a full address into the visible box but
        // never actually chose a dropdown suggestion. A 'click'
        // listener on the submit button runs synchronously BEFORE the
        // browser's own native constraint-validation/submit sequence
        // for that same click, so the canonical field is guaranteed
        // current by the time that validation runs — never a race,
        // never dependent on any event PlaceAutocompleteElement
        // doesn't actually fire (it has no keystroke/input event; see
        // this file's own header comment).
        if (submitButton) {
          submitButton.addEventListener('click', function () {
            var visibleValue = autocomplete && typeof autocomplete.value === 'string' ? autocomplete.value.trim() : '';
            if (visibleValue) manualInput.value = visibleValue;
          });
        }

        if (countrySelect) {
          countrySelect.addEventListener('change', function () {
            // A country change invalidates whatever was typed/selected
            // for the PREVIOUS country — re-create rather than mutate,
            // so no stale Place/session state survives. City/state/
            // postal are cleared too (see this file's own header
            // comment): they may have come from a Google selection in
            // the old country and would otherwise silently go stale.
            createAutocomplete(currentCountryCode());
            var cityField = document.querySelector('[name="city"]');
            if (cityField) cityField.value = '';
            var provinceField = document.querySelector('[name="province"]');
            if (provinceField) provinceField.value = '';
            var postalField = document.querySelector('[name="postal_code"]');
            if (postalField) postalField.value = '';
          });
        }
      })
      .catch(function () {
        // Graceful fallback — the manual input was never hidden/
        // replaced in this branch (that only happens after the
        // library successfully resolves above), so nothing to restore.
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
